import {
    VisitOrderQueryQuery,
    VisitOrderQueryQueryVariables,
} from '@/components/visitOrders/VisitOrdersTable.gql.types';
import { VisitOrderForListDisplay } from '@/fetching/sharedSchemas/visitOrders';
import { renderVisitOrderState } from '@/types/visitOrders';
import { gql, useSuspenseQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { VisitOrderState } from 'gql-types';
import moment, { Moment } from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';

import { AxleChip } from '../displayFields/chips';
import {
    AXLE_TABLE_NONE_TEXT,
    AxleTable,
    DEFAULT_PAGE_SIZE_OPTIONS,
    ExtraAxleDataTableProps,
    RawColumn,
} from '../tables/AxleTable';
import { VerticalStack } from '../utils/linearLayouts';
import { usePaginationByPage } from '../utils/pagination';

export const VisitOrdersListItemFragment = gql`
    fragment VisitOrdersListItemFragment on VisitOrder {
        id
        state
        fromDate
        toDate
        minVisitsPerUnitTime
        maxVisitsPerUnitTime
        serviceBundles {
            id
            name
        }
        patient {
            fullName
        }
    }
`;

export type VisitOrdersListItemType = {
    id: string;
    state: VisitOrderState;
    fromDate: Date | null;
    toDate: Date | null;
    minVisitsPerUnitTime: number | null;
    maxVisitsPerUnitTime: number | null;
    serviceBundles: { id: string; name: string }[];
    patient: { fullName: string };
};

export const GET_VISIT_ORDERS_LIST_QUERY = gql`
    query VisitOrderQuery(
        $filters: VisitOrderFilter
        $pagination: PaginationInput
        $ordering: VisitOrderOrdering
    ) {
        visitOrders(filters: $filters, pagination: $pagination, ordering: $ordering) {
            items {
                ...VisitOrdersListItemFragment
            }
            totalCount
            hasMore
        }
    }
`;

export type VisitOrdersListQueryType = {
    visitOrders: {
        items: VisitOrdersListItemType[];
        totalCount: number;
        hasMore: boolean;
    };
};

const columnIf = ({
    condition,
    column,
}: {
    condition: boolean;
    column: RawColumn;
}): RawColumn[] => {
    return condition ? [column] : [];
};

// TODO: Rename component to not be `-Content` (doesn't follow established naming pattern)
// when we have a better name for the component.
export const VisitOrdersTableContent = ({
    serviceOrders,
    hidePatient = false,
    title = undefined,
    onRowClick = undefined,
    tableOptions = {},
}: {
    serviceOrders: NonNullable<VisitOrderQueryQuery['visitOrders']>['items'];
    hidePatient?: boolean;
    title?: string;
    onRowClick?: (
        serviceOrder: NonNullable<VisitOrderQueryQuery['visitOrders']>['items'][number],
    ) => void;
    tableOptions?: ExtraAxleDataTableProps<
        NonNullable<VisitOrderQueryQuery['visitOrders']>['items'][number]
    >;
}) => {
    return (
        <AxleTable
            title={title !== undefined ? <Typography variant='h2'>{title}</Typography> : undefined}
            data={serviceOrders}
            onRowClick={onRowClick}
            rowToLink={(serviceOrder) => `/visit-orders/${serviceOrder.id}`}
            columns={[
                ...columnIf({
                    condition: !hidePatient,
                    column: {
                        label: 'Patient',
                        field: 'patient.fullName',
                    },
                }),
                {
                    label: 'Status',
                    rowToComponent: (serviceOrder) => (
                        <AxleChip label={renderVisitOrderState(serviceOrder.state)} />
                    ),
                },
                {
                    label: 'Services',
                    rowToComponent: (serviceOrder) =>
                        serviceOrder.serviceBundles?.length ? (
                            <Box sx={{ maxWidth: '150px', overflowX: 'hidden' }}>
                                <VerticalStack spacing={1}>
                                    <AxleChip
                                        label={serviceOrder.serviceBundles[0].name}
                                        key={serviceOrder.serviceBundles[0].id}
                                    />
                                </VerticalStack>
                            </Box>
                        ) : null,
                },
                {
                    label: 'Start Date',
                    field: 'fromDate',
                    fieldToText: (fromDate: Date | null) =>
                        fromDate === null
                            ? AXLE_TABLE_NONE_TEXT
                            : moment(fromDate).format('YYYY-MM-DD'),
                },
                {
                    label: 'End Date',
                    field: 'toDate',
                    fieldToText: (toDate: Date | null) =>
                        toDate === null
                            ? AXLE_TABLE_NONE_TEXT
                            : moment(toDate).format('YYYY-MM-DD'),
                },
                {
                    label: 'Scheduling Constraints',
                    rowToComponent: (serviceOrder) =>
                        serviceOrder.minVisitsPerUnitTime || serviceOrder.maxVisitsPerUnitTime ? (
                            <p>
                                {serviceOrder.minVisitsPerUnitTime ?? AXLE_TABLE_NONE_TEXT} to{' '}
                                {serviceOrder.maxVisitsPerUnitTime ?? AXLE_TABLE_NONE_TEXT} visits
                                per Week
                            </p>
                        ) : null,
                },
            ]}
            {...tableOptions}
        />
    );
};

export const VisitOrdersTable = ({ selectedState }: { selectedState: VisitOrderState | 'all' }) => {
    // TODO: Do we want page/pageSize to be in an atom?
    const {
        dynamicPaging: { page, pageSize, setPage, setPageSize },
        paginationInput,
        preloadNextPageInput,
        preloadPreviousPageInput,
    } = usePaginationByPage();

    // We should reset the page when the selected state changes. Since you are looking at a
    // new slice of the data, you should be looking at the first page for that slice.
    useEffect(() => {
        setPage(0);
    }, [selectedState, setPage]);

    const [searchTerm, setSearchTerm] = useState<string>('');

    const variables = useMemo(
        () => ({
            pagination: paginationInput,
            ordering: undefined,
            filters: {
                ...(selectedState !== 'all' && { state: { exact: selectedState } }),
                ...(searchTerm && { searchTerm: searchTerm }),
            },
        }),
        [paginationInput, selectedState, searchTerm],
    );

    const { data, client } = useSuspenseQuery<VisitOrderQueryQuery, VisitOrderQueryQueryVariables>(
        GET_VISIT_ORDERS_LIST_QUERY,
        { variables },
    );
    // TODO: Use hasMore to determine if we should prefetch the next page.
    const { items: serviceOrders, totalCount, hasMore } = data.visitOrders ?? {};

    useEffect(() => {
        // void is used because we are calling an async function but we don't care to wait
        // for the query to finish. We just want to make sure the query is ran for prefetching
        if (preloadPreviousPageInput) {
            void client.query<VisitOrderQueryQuery, VisitOrderQueryQueryVariables>({
                query: GET_VISIT_ORDERS_LIST_QUERY,
                variables: {
                    ...variables,
                    pagination: preloadPreviousPageInput,
                },
            });
        }
        if (hasMore) {
            void client.query<VisitOrderQueryQuery, VisitOrderQueryQueryVariables>({
                query: GET_VISIT_ORDERS_LIST_QUERY,
                variables: {
                    ...variables,
                    pagination: preloadNextPageInput,
                },
            });
        }
    }, [variables, preloadPreviousPageInput, preloadNextPageInput, hasMore, client]);

    return (
        <VisitOrdersTableContent
            serviceOrders={serviceOrders ?? []}
            tableOptions={{
                searchState: { searchTerm, setSearchTerm },
                dynamicPaging: {
                    page: page,
                    setPage: setPage,
                    pageSize: pageSize,
                    setPageSize: setPageSize,
                    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                    numRows: totalCount ?? 0,
                },
                // TODO: Because of pagination, we now need to filter on the backend.
                filter: false,
            }}
        />
    );
};

export const VisitOrdersTableDeprecated = ({
    serviceOrders,
    hidePatient = false,
    title = undefined,
    onRowClick = undefined,
    tableOptions = {},
}: {
    serviceOrders: VisitOrderForListDisplay[];
    hidePatient?: boolean;
    title?: string;
    onRowClick?: (serviceOrder: VisitOrderForListDisplay) => void;
    tableOptions?: ExtraAxleDataTableProps<VisitOrderForListDisplay>;
}) => {
    return (
        <AxleTable
            title={title !== undefined ? <Typography variant='h2'>{title}</Typography> : undefined}
            data={serviceOrders}
            onRowClick={onRowClick}
            rowToLink={(serviceOrder) => `/visit-orders/${serviceOrder.uuid}`}
            columns={[
                ...columnIf({
                    condition: !hidePatient,
                    column: {
                        label: 'Patient',
                        field: 'patient.name',
                    },
                }),
                {
                    label: 'Status',
                    rowToComponent: (serviceOrder: VisitOrderForListDisplay) => (
                        <AxleChip label={serviceOrder.state.name} />
                    ),
                },
                {
                    label: 'Services',
                    rowToComponent: (serviceOrder: VisitOrderForListDisplay) => (
                        <Box sx={{ maxWidth: '150px', overflowX: 'hidden' }}>
                            <VerticalStack spacing={1}>
                                <AxleChip
                                    label={serviceOrder.service_bundle.name}
                                    key={serviceOrder.service_bundle.uuid}
                                />
                            </VerticalStack>
                        </Box>
                    ),
                },
                {
                    label: 'Start Date',
                    field: 'from_date',
                    fieldToText: (fromDate: Moment | null) => fromDate?.format('YYYY-MM-DD') ?? '',
                },
                {
                    label: 'End Date',
                    field: 'to_date',
                    fieldToText: (toDate: Moment | null) => toDate?.format('YYYY-MM-DD') ?? '',
                },
                {
                    label: 'Scheduling Constraints',
                    rowToComponent: (serviceOrder: VisitOrderForListDisplay) =>
                        serviceOrder.min_visits_per_unit_time ||
                        serviceOrder.max_visits_per_unit_time ? (
                            <p>
                                {serviceOrder.min_visits_per_unit_time ?? AXLE_TABLE_NONE_TEXT} to{' '}
                                {serviceOrder.max_visits_per_unit_time ?? AXLE_TABLE_NONE_TEXT}{' '}
                                visits per Week
                            </p>
                        ) : null,
                },
            ]}
            {...tableOptions}
        />
    );
};
